import React, { useCallback, useMemo } from 'react';

import { HistoryTransforms } from '@aline/common';
import { ClipboardPaste } from 'lucide-react';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { CopyIcon } from 'src/components/icons/CopyIcon';
import { CutIcon } from 'src/components/icons/CutIcon';
import { RedoIcon } from 'src/components/icons/RedoIcon';
import { SearchIcon } from 'src/components/icons/SearchIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { UndoIcon } from 'src/components/icons/UndoIcon';
import { useEditorFindStateActions } from 'src/editor/editor-find-state-registry';
import { useCurrentUserId } from 'src/gql';
import { useDispatchSuccessSnack, useDocumentParams } from 'src/helpers';
import { useAuthorizedTransforms } from 'src/hooks';
import { editorHotkeys, handlePaste, slateHotkeys } from 'src/layout-engine';
import { useEditor } from 'src/layout-engine/hooks';
import { useCurrentEditingMode, useCurrentUserPermissionRole } from 'src/store/document/selectors';
import { v4 as uuid4 } from 'uuid';

import { ClientTransforms } from '../../transforms';
import { useIfEditableMixin, useUnlessViewerMixin } from '../shared/menu-mixins';

export function useEditMenu() {
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { canEdit } = useCurrentUserPermissionRole();
  const findActions = useEditorFindStateActions();
  const editor = useEditor();
  const clipboardMenu = useClipboardMenu();
  const unlessViewer = useUnlessViewerMixin();

  const findAndReplace = useCallback(() => findActions.setSearch(''), [findActions]);

  const redo = useCallback(() => {
    HistoryTransforms.redo(editor);
    ClientTransforms.focus(editor);
  }, [editor]);

  const selectAll = useCallback(() => {
    AuthorizedTransforms.selectAll(editor);
    ClientTransforms.focus(editor);
  }, [AuthorizedTransforms, editor]);

  const undo = useCallback(() => {
    HistoryTransforms.undo(editor);
    ClientTransforms.focus(editor);
  }, [editor]);

  return useMemo(
    (): DeclarativeMenuConfig => [
      {
        hint: editorHotkeys.undo.hint,
        icon: <UndoIcon />,
        label: 'Undo',
        onClick: undo,
        ...unlessViewer,
      },
      {
        hint: editorHotkeys.redo.hint,
        icon: <RedoIcon />,
        label: 'Redo',
        onClick: redo,
        ...unlessViewer,
      },
      DECLARATIVE_MENU_SEPARATOR,
      ...clipboardMenu,
      { hint: editorHotkeys.selectAll.hint, label: 'Select all', onClick: selectAll },
      {
        hint: slateHotkeys.search.hint,
        icon: <SearchIcon />,
        label: canEdit ? 'Find and replace...' : 'Find...',
        onClick: findAndReplace,
      },
    ],
    [canEdit, clipboardMenu, findAndReplace, redo, selectAll, undo, unlessViewer]
  );
}

/** Cut, Copy, Paste and Delete, shared by the Edit menu and the document context menu. */
export function useClipboardMenu(): DeclarativeMenuConfig {
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const dispatchCopySuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Content copied to clipboard.',
  });
  const editingMode = useCurrentEditingMode();
  const editor = useEditor();
  const { documentId } = useDocumentParams();
  const ifEditable = useIfEditableMixin();

  const copy = useCallback(async () => {
    const isCopied = await ClientTransforms.copyToClipboard(editor, { documentId });
    if (isCopied) dispatchCopySuccessSnack();
    ClientTransforms.focus(editor);
  }, [dispatchCopySuccessSnack, documentId, editor]);

  const cut = useCallback(async () => {
    if (!currentUserId || !editingMode) return;
    const isCopied = await ClientTransforms.copyToClipboard(editor, { documentId });
    if (isCopied)
      AuthorizedTransforms.deleteContent(editor, {
        authorId: currentUserId,
        direction: 'backward',
        editingMode,
      });
    ClientTransforms.focus(editor);
  }, [AuthorizedTransforms, currentUserId, documentId, editingMode, editor]);

  const deleteText = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.deleteContent(editor, {
      authorId: currentUserId,
      direction: 'backward',
      editingMode,
    });
    ClientTransforms.focus(editor);
  }, [AuthorizedTransforms, currentUserId, editingMode, editor]);

  const paste = useCallback(async () => {
    if (!currentUserId || !editingMode) return;
    await handlePaste(editor, { authorId: currentUserId, documentId, editingMode });
    ClientTransforms.focus(editor);
  }, [currentUserId, documentId, editingMode, editor]);

  return useMemo(
    (): DeclarativeMenuConfig => [
      {
        hint: editorHotkeys.cut.hint,
        icon: <CutIcon />,
        label: 'Cut',
        onClick: cut,
        ...ifEditable,
      },
      { hint: editorHotkeys.copy.hint, icon: <CopyIcon />, label: 'Copy', onClick: copy },
      {
        hint: editorHotkeys.paste.hint,
        icon: <ClipboardPaste />,
        label: 'Paste',
        onClick: paste,
        ...ifEditable,
      },
      { icon: <TrashcanIcon />, label: 'Delete', onClick: deleteText, ...ifEditable },
    ],
    [copy, cut, deleteText, ifEditable, paste]
  );
}
